import { AnnualStatementPageFilter } from "@models/annual-statements/annual-statement-enums";
import { IPartialHeaderData } from "@shared/services/header.service";

export enum AnnualStatementsGridColumns {
  CompanyNameAcn,
  Acn,
  Status,
  DebtStatus,
  Partner,
  ReviewDate,
  PaymentDeadline,
  Debt,
  Tags,
  TagsName,
  SigningStatus,
  SigningDate,
  AccountManager,
  PaymentReminders,
  SigningReminders,
}

export interface IAnnualStatementsPageConfig {
  key: AnnualStatementPageFilter;
  storageKey: string;
  pageTitle: string;
  sheetName: string;
  headerData: IPartialHeaderData;
  colDefsKeys: AnnualStatementsGridColumns[];
}

function getAnnualStatementsReadyToSendConfig(): IAnnualStatementsPageConfig {
  return {
    key: AnnualStatementPageFilter.ReadyToSend,
    storageKey: 'annual-ready-to-send',
    pageTitle: 'Ready to Send',
    sheetName: 'Annual Statements Ready to Send',
    headerData: {
      title: 'Annual Statements',
      subtitle: 'Ready to Send',
      iconClass: 'ic-annual-statement-20'
    },
    colDefsKeys: [
      AnnualStatementsGridColumns.CompanyNameAcn,
      AnnualStatementsGridColumns.Acn,
      AnnualStatementsGridColumns.Partner,
      AnnualStatementsGridColumns.Status,
      AnnualStatementsGridColumns.DebtStatus,
      AnnualStatementsGridColumns.ReviewDate,
      AnnualStatementsGridColumns.PaymentDeadline,
      AnnualStatementsGridColumns.Debt,
    ]
  };
}

function getAnnualStatementsNotReceivedConfig(): IAnnualStatementsPageConfig {
  return {
    key: AnnualStatementPageFilter.NotReceived,
    storageKey: 'annual-not-received',
    pageTitle: 'Not Received',
    sheetName: 'Annual Statements Not Received',
    headerData: {
      title: 'Annual Statements',
      subtitle: 'Not Received',
      iconClass: 'ic-annual-statement-20'
    },
    colDefsKeys: [
      AnnualStatementsGridColumns.CompanyNameAcn,
      AnnualStatementsGridColumns.Acn,
      AnnualStatementsGridColumns.Partner,
      AnnualStatementsGridColumns.ReviewDate,
      AnnualStatementsGridColumns.Debt,
    ]
  };
}

function getAnnualStatementsExpectedConfig(): IAnnualStatementsPageConfig {
  return {
    key: AnnualStatementPageFilter.Expected,
    storageKey: 'annual-expected',
    pageTitle: 'Expected',
    sheetName: 'Annual Statements Expected',
    headerData: {
      title: 'Annual Statements',
      subtitle: 'Expected',
      iconClass: 'ic-annual-statement-20'
    },
    colDefsKeys: [
      AnnualStatementsGridColumns.CompanyNameAcn,
      AnnualStatementsGridColumns.Acn,
      AnnualStatementsGridColumns.Partner,
      AnnualStatementsGridColumns.ReviewDate,
      AnnualStatementsGridColumns.Tags,
      AnnualStatementsGridColumns.TagsName,
      AnnualStatementsGridColumns.AccountManager,
    ]
  };
}

function getAnnualStatementsSentConfig(): IAnnualStatementsPageConfig {
  return {
    key: AnnualStatementPageFilter.Sent,
    storageKey: 'annual-sent',
    pageTitle: 'Sent',
    sheetName: 'Annual Statements Sent',
    headerData: {
      title: 'Annual Statements',
      subtitle: 'Sent',
      iconClass: 'ic-annual-statement-20'
    },
    colDefsKeys: [
      AnnualStatementsGridColumns.CompanyNameAcn,
      AnnualStatementsGridColumns.Acn,
      AnnualStatementsGridColumns.Status,
      AnnualStatementsGridColumns.DebtStatus,
      AnnualStatementsGridColumns.Partner,
      AnnualStatementsGridColumns.ReviewDate,
      AnnualStatementsGridColumns.PaymentDeadline,
      AnnualStatementsGridColumns.Debt,
      AnnualStatementsGridColumns.Tags,
      AnnualStatementsGridColumns.TagsName,
      AnnualStatementsGridColumns.SigningStatus,
      AnnualStatementsGridColumns.PaymentReminders,
      AnnualStatementsGridColumns.SigningReminders,
      AnnualStatementsGridColumns.AccountManager,
    ]
  };
}

function getAnnualStatementsPaymentDueConfig(): IAnnualStatementsPageConfig {
  return {
    key: AnnualStatementPageFilter.PaymentDue,
    storageKey: 'annual-payment-due',
    pageTitle: 'Payment Due',
    sheetName: 'Annual Statements Payment Due',
    headerData: {
      title: 'Annual Statements',
      subtitle: 'Payment Due',
      iconClass: 'ic-annual-statement-20'
    },
    colDefsKeys: [
      AnnualStatementsGridColumns.CompanyNameAcn,
      AnnualStatementsGridColumns.Acn,
      AnnualStatementsGridColumns.Status,
      AnnualStatementsGridColumns.DebtStatus,
      AnnualStatementsGridColumns.Partner,
      AnnualStatementsGridColumns.ReviewDate,
      AnnualStatementsGridColumns.PaymentDeadline,
      AnnualStatementsGridColumns.Debt,
      AnnualStatementsGridColumns.Tags,
      AnnualStatementsGridColumns.TagsName,
      AnnualStatementsGridColumns.SigningStatus,
      AnnualStatementsGridColumns.AccountManager,
      AnnualStatementsGridColumns.PaymentReminders,
      AnnualStatementsGridColumns.SigningReminders,
    ]
  };
}

function getAnnualStatementsOverdueConfig(): IAnnualStatementsPageConfig {
  return {
    key: AnnualStatementPageFilter.Overdue,
    storageKey: 'annual-overdue',
    pageTitle: 'Overdue',
    sheetName: 'Annual Statements Overdue',
    headerData: {
      title: 'Annual Statements',
      subtitle: 'Overdue',
      iconClass: 'ic-annual-statement-20'
    },
    colDefsKeys: [
      AnnualStatementsGridColumns.CompanyNameAcn,
      AnnualStatementsGridColumns.Acn,
      AnnualStatementsGridColumns.Status,
      AnnualStatementsGridColumns.DebtStatus,
      AnnualStatementsGridColumns.Partner,
      AnnualStatementsGridColumns.ReviewDate,
      AnnualStatementsGridColumns.PaymentDeadline,
      AnnualStatementsGridColumns.Debt,
      AnnualStatementsGridColumns.Tags,
      AnnualStatementsGridColumns.TagsName,
      AnnualStatementsGridColumns.SigningStatus,
      AnnualStatementsGridColumns.AccountManager,
      AnnualStatementsGridColumns.PaymentReminders,
      AnnualStatementsGridColumns.SigningReminders,
    ]
  };
}

function getAnnualStatementsArchivedConfig(): IAnnualStatementsPageConfig {
  return {
    key: AnnualStatementPageFilter.Archived,
    storageKey: 'annual-archived',
    pageTitle: 'Archived',
    sheetName: 'Annual Statements Archived',
    headerData: {
      title: 'Annual Statements',
      subtitle: 'Archived',
      iconClass: 'ic-annual-statement-20'
    },
    colDefsKeys: [
      AnnualStatementsGridColumns.CompanyNameAcn,
      AnnualStatementsGridColumns.Acn,
      AnnualStatementsGridColumns.Status,
      AnnualStatementsGridColumns.Partner,
      AnnualStatementsGridColumns.ReviewDate,
      AnnualStatementsGridColumns.PaymentDeadline,
      AnnualStatementsGridColumns.Debt,
      AnnualStatementsGridColumns.Tags,
      AnnualStatementsGridColumns.TagsName,
      AnnualStatementsGridColumns.SigningStatus,
      AnnualStatementsGridColumns.AccountManager,
    ]
  };
}

function getAnnualStatementsAllConfig(): IAnnualStatementsPageConfig {
  return {
    key: AnnualStatementPageFilter.All,
    storageKey: 'annual-all',
    pageTitle: 'All',
    sheetName: 'Annual statements',
    headerData: {
      title: 'Annual Statements',
      subtitle: 'All',
      iconClass: 'ic-annual-statement-20'
    },
    colDefsKeys: [
      AnnualStatementsGridColumns.CompanyNameAcn,
      AnnualStatementsGridColumns.Acn,
      AnnualStatementsGridColumns.Status,
      AnnualStatementsGridColumns.DebtStatus,
      AnnualStatementsGridColumns.SigningStatus,
      AnnualStatementsGridColumns.Partner,
      AnnualStatementsGridColumns.ReviewDate,
      AnnualStatementsGridColumns.PaymentDeadline,
      AnnualStatementsGridColumns.Debt,
      AnnualStatementsGridColumns.Tags,
      AnnualStatementsGridColumns.TagsName,
      AnnualStatementsGridColumns.PaymentReminders,
      AnnualStatementsGridColumns.SigningReminders,
      AnnualStatementsGridColumns.AccountManager,
    ]
  };
}

const annualStatementsPageConfigsRecord: Record<AnnualStatementPageFilter, IAnnualStatementsPageConfig> = {
  [AnnualStatementPageFilter.Burning]: {} as any as IAnnualStatementsPageConfig,
  [AnnualStatementPageFilter.ReadyToSend]: getAnnualStatementsReadyToSendConfig(),
  [AnnualStatementPageFilter.NotReceived]: getAnnualStatementsNotReceivedConfig(),
  [AnnualStatementPageFilter.Expected]: getAnnualStatementsExpectedConfig(),
  [AnnualStatementPageFilter.Sent]: getAnnualStatementsSentConfig(),
  [AnnualStatementPageFilter.PaymentDue]: getAnnualStatementsPaymentDueConfig(),
  [AnnualStatementPageFilter.Overdue]: getAnnualStatementsOverdueConfig(),
  [AnnualStatementPageFilter.Archived]: getAnnualStatementsArchivedConfig(),
  [AnnualStatementPageFilter.All]: getAnnualStatementsAllConfig(),
};

export function getAnnualStatementsPageConfig(annualStatementPageFilter: AnnualStatementPageFilter): IAnnualStatementsPageConfig {
  return annualStatementsPageConfigsRecord[annualStatementPageFilter];
}
